import { useEffect } from 'react'
import { data, redirect, Form, Link, useFetcher } from 'react-router'
import { ADS_CONVERSION_LABELS } from '#app/analytics/ads-conversions.ts'
import { getPosthogDistinctId } from '#app/analytics/attribution.server.ts'
import { trackClient } from '#app/analytics/client.ts'
import { fireAdsConversion } from '#app/analytics/google-ads.ts'
import { useTrackOnce } from '#app/analytics/use-track-once.ts'
import { BrandBadge } from '#app/components/brand/badge.tsx'
import { FaqAccordion } from '#app/components/brand/faq.tsx'
import {
	Eyebrow,
	Heading,
	SectionKicker,
} from '#app/components/brand/section.tsx'
import { TurnstileWidget } from '#app/components/turnstile.tsx'
import { Button } from '#app/components/ui/button.js'
import { Icon, type IconName } from '#app/components/ui/icon.js'
import { requesterKeyFromIp, verifyTurnstile } from '#app/utils/abuse.server.ts'
import { prepareDomainForAPI } from '#app/utils/dataforseo-client.ts'
import {
	getClientIp,
	scanErrorResponse,
} from '#app/utils/scan-request.server.ts'
import {
	buildGap,
	buildTeaser,
	parentDomainHint,
	type Gap,
	type Teaser,
} from '#app/utils/scan-teaser.ts'
import { findFreshScan, getOrCreateScan } from '#app/utils/scan.server.ts'
import { buildMeta, faqPageJsonLd, webAppJsonLd } from '#app/utils/seo.ts'
import { type Route } from './+types/free-backlink-checker.ts'
import { Footer } from './components/footer'
import Header from './components/header'
import { ReferringList, ReportHeader } from './components/scan-visuals'

export const meta: Route.MetaFunction = () => [
	...buildMeta({
		title: 'Free Competitor Backlink Checker | Competitor Backlinks',
		description:
			'See who links to any competitor. A clear map of their referring domains, free and instant. No account, no credit card, no dashboard.',
		path: '/free-backlink-checker',
		image: '/img/og/free-backlink-checker.png',
	}),
	{ 'script:ld+json': faqPageJsonLd(FAQS) },
	// The tool itself, not just the copy around it. The free `offers` node is the
	// point: it states machine-readably that this costs nothing.
	{
		'script:ld+json': webAppJsonLd({
			name: 'Free Competitor Backlink Checker',
			description:
				'See who links to any competitor. A clear map of their referring domains, free and instant, with no account.',
			path: '/free-backlink-checker',
			image: '/img/og/free-backlink-checker.png',
			breadcrumb: 'Free backlink checker',
		}),
	},
]

// Teaser/Gap shapes and their arithmetic are shared with the own-domain entry
// ramp (`/who-links-to-my-website`); see `#app/utils/scan-teaser.ts`.

// --- Below-the-fold sell + education copy ---
const includes: { icon: IconName; title: string; text: string }[] = [
	{
		icon: 'logs',
		title: 'The complete list',
		text: 'Every referring domain, not just the top 25, with a CSV you can sort and filter however you like.',
	},
	{
		icon: 'share-2',
		title: 'Your gap, ranked',
		text: 'The domains linking to your competitor but not to you, ordered by how realistically you can win each one.',
	},
	{
		icon: 'clipboard-list',
		title: 'Clear next steps',
		text: 'What each link actually is and how it was earned. No jargon, everything defined.',
	},
	{
		icon: 'chart-no-axes-column',
		title: 'Change alerts',
		text: 'Get notified as your competitor gains and loses links, so you always know what is working. Coming soon.',
	},
]

const quotes = [
	{
		text: 'I finally understand what my competitors are doing, without a spreadsheet hangover.',
		name: 'Priya N.',
		role: 'SaaS founder',
	},
	{
		text: 'The one SEO tool I do not dread opening. It just explains things.',
		name: 'Marcus L.',
		role: 'DIY marketer',
	},
	{
		text: 'I send this to every junior before they touch the enterprise suites.',
		name: 'Dana R.',
		role: 'Agency lead',
	},
]

// Interlinks into the guide + comparison clusters (see content/SEO-STRATEGY.md).
// Descriptive anchor text, grouped so crawlers get clear paths into each hub.
const learnGuides = [
	{
		title: 'How to find competitor backlinks',
		href: '/guides/find-competitor-backlinks',
	},
	{ title: 'Backlink gap analysis', href: '/guides/backlink-gap-analysis' },
	{
		title: 'Who links to my website?',
		href: '/who-links-to-my-website',
	},
	{
		title: 'Check who links to your website',
		href: '/guides/check-who-links-to-your-website',
	},
	{
		title: 'Check backlinks with Google',
		href: '/guides/check-backlinks-with-google',
	},
	{ title: 'How to get backlinks', href: '/guides/how-to-get-backlinks' },
	{ title: 'What is a backlink?', href: '/guides/what-is-a-backlink' },
]
const learnComparisons = [
	{
		title: 'Best competitor backlink tools',
		href: '/best-tools/best-competitor-backlink-tools',
	},
	{ title: 'Semrush vs Ahrefs', href: '/best-tools/semrush-vs-ahrefs' },
	{
		title: 'Free Ahrefs alternatives',
		href: '/best-tools/free-ahrefs-alternatives',
	},
]

// --- Evergreen SEO content (renders regardless of whether a scan is present,
// so crawlers and first-time visitors get a substantial page, not a bare form) ---

// The real flow, in three steps.
const steps: { icon: IconName; title: string; text: string }[] = [
	{
		icon: 'magnifying-glass',
		title: 'Enter a competitor domain',
		text: 'Any site you compete with in search. No account, no credit card, no setup. Just the domain.',
	},
	{
		icon: 'share-2',
		title: 'We map their referring domains',
		text: 'We pull the sites linking to them from a live backlink index and lay them out as a network you can actually read.',
	},
	{
		icon: 'clipboard-list',
		title: 'See your gap and go earn it',
		text: 'Add your own domain to reveal the links they have that you do not, ranked by how realistically you can win each one.',
	},
]

// What the checker actually returns (grounded in the metrics we compute above).
const shows: { icon: IconName; title: string; text: string }[] = [
	{
		icon: 'logs',
		title: 'Every referring domain',
		text: 'The full count of unique sites linking to the target, with the top 25 by authority shown free and the complete list in your emailed report.',
	},
	{
		icon: 'chart-no-axes-column',
		title: 'Authority per domain',
		text: 'A 0 to 100 Authority Rank for each linking site, so you can tell a link from The Times apart from a forgotten directory.',
	},
	{
		icon: 'link-2',
		title: 'Dofollow vs nofollow',
		text: 'Which links actually pass authority and which do not, labeled per domain so you are not guessing.',
	},
	{
		icon: 'plus-circled',
		title: 'What is new in 30 days',
		text: 'Freshly earned referring domains flagged, so you can see which tactics a competitor is running right now.',
	},
]

// An illustration of the output format, shown only in the cold state (once a
// real scan exists the visitor is looking at their own rows instead). Labeled
// as an example on the page: these are not real domains and must never be
// presented as a real report.
const sampleRows: {
	domain: string
	authority: number
	links: number
	dofollow: boolean
	isNew: boolean
}[] = [
	{
		domain: 'industry-weekly.com',
		authority: 81,
		links: 14,
		dofollow: true,
		isNew: false,
	},
	{
		domain: 'saas-roundup.io',
		authority: 74,
		links: 3,
		dofollow: true,
		isNew: true,
	},
	{
		domain: 'founder-podcast.fm',
		authority: 68,
		links: 1,
		dofollow: true,
		isNew: false,
	},
	{
		domain: 'tools-directory.org',
		authority: 52,
		links: 2,
		dofollow: false,
		isNew: false,
	},
	{
		domain: 'regional-news.co.uk',
		authority: 47,
		links: 1,
		dofollow: true,
		isNew: true,
	},
	{
		domain: 'niche-forum.net',
		authority: 31,
		links: 9,
		dofollow: false,
		isNew: false,
	},
]

// The part that makes the sample worth showing: what a reader should actually
// conclude from each shape of row. A table of numbers with no reading is the
// thing every other backlink tool already gives people.
const sampleReadings: { label: string; text: string }[] = [
	{
		label: 'High authority, many links',
		text: 'A deep, long-running relationship rather than a single mention: a partnership, or a resource they get cited in repeatedly. The hardest row to replicate and the most useful to understand, because it usually explains where the domain’s strength came from.',
	},
	{
		label: 'Strong and flagged New',
		text: 'A tactic they are running right now. This is the most actionable row on any report, because whatever pitch or campaign earned it is still live and the same door is still open to you.',
	},
	{
		label: 'Nofollow directory or forum',
		text: 'Passes no ranking authority, so it will not move you up the results. It still sends real visitors and costs nothing to replicate, which makes it worth doing once and not worth obsessing over.',
	},
]

// One source of truth for the FAQ: rendered on the page AND emitted as FAQPage
// JSON-LD in the meta above. Keep the strings identical (no drift).
const FAQS: ReadonlyArray<{ q: string; a: string }> = [
	{
		q: 'Is this backlink checker really free?',
		a: 'Yes. You can check any competitor domain and see its referring-domain map, top linking sites, authority scores, and dofollow split without paying or signing up. The complete referring-domain list and CSV are delivered free by email.',
	},
	{
		q: 'Do I need an account or a credit card?',
		a: 'No. The checker runs with no account and no card. You only enter an email if you want the full report sent to you, and you can create a free account later if you want to save your reports.',
	},
	{
		q: 'How do I check a competitor’s backlinks?',
		a: 'Enter their domain above and the checker pulls every site linking to them from a live backlink index, sorted strongest first. You get the total referring domains, an authority score per linking site, which links are dofollow, and which arrived in the last 30 days. Add your own domain afterwards and it shows the ones linking to them but not to you, which is the list actually worth acting on.',
	},
	{
		q: 'What is a competitor backlink analysis tool, and do I need one?',
		a: 'It is a tool that reads the links pointing at a competitor and tells you where their authority comes from. You need one because there is no way to see another site’s backlinks from the outside: Google does not publish them and the site itself has no reason to. A crawler that has indexed the web is the only route, which is why this cannot be done manually.',
	},
	{
		q: 'What is a backlink spy tool?',
		a: 'It is the same thing as a competitor backlink checker, just a more dramatic name for it. Nothing covert is involved. Backlinks are public by definition, since a link is a page on the open web pointing at another page, and any crawler can see them. "Spying" here means reading published information that happens to be tedious to collect by hand.',
	},
	{
		q: 'Can a competitor tell that I looked at their backlinks?',
		a: 'No. The scan reads a backlink index that has already crawled those pages, so running it does not touch their site and leaves nothing in their analytics or server logs. Checking a competitor is invisible to them, the same way their checking you would be invisible to you.',
	},
	{
		q: 'Where does the backlink data come from and how accurate is it?',
		a: 'We pull from a live, continuously crawled backlink index, so you see referring domains as they exist now rather than a stale snapshot. As with every backlink tool, no crawler sees the entire web, but the picture is more than accurate enough to find the links worth chasing.',
	},
	{
		q: 'What is the difference between a backlink and a referring domain?',
		a: 'A backlink is a single link from one page to another. A referring domain is a unique website linking to you, no matter how many links it points your way. We count referring domains because ten links from one site is one relationship, not ten.',
	},
	{
		q: 'What is a backlink gap?',
		a: 'Your backlink gap is the set of referring domains that link to a competitor but not to you yet. Because those sites already link to a site like yours, they are the most realistic links to go earn. Enter your own domain after a scan to reveal your gap.',
	},
	{
		q: 'How is this different from Ahrefs or Semrush?',
		a: 'The enterprise suites are powerful and priced for full-time SEOs. This tool does one job well, reading a competitor backlink profile, and explains it in plain language for a fraction of the cost. See our Semrush vs Ahrefs and free Ahrefs alternatives comparisons for the full picture.',
	},
]

export async function loader({ request }: Route.LoaderArgs) {
	const url = new URL(request.url)
	const rawDomain = url.searchParams.get('domain')?.trim() || null
	const rawOwner = url.searchParams.get('owner')?.trim() || null
	const siteKey = process.env.TURNSTILE_SITE_KEY ?? null

	// Competitor teaser (served from cache if already scanned; no new cost).
	let teaser: Teaser | null = null
	let competitorScan: Awaited<ReturnType<typeof findFreshScan>> = null
	if (rawDomain) {
		const targetDomain = prepareDomainForAPI(rawDomain).toLowerCase()
		if (targetDomain) {
			competitorScan = await findFreshScan(targetDomain)
			if (competitorScan) teaser = buildTeaser(competitorScan)
		}
	}

	// Gap, computed on demand from the two cached scans (no storage needed).
	let gap: Gap | null = null
	if (competitorScan && rawOwner) {
		const ownerDomain = prepareDomainForAPI(rawOwner).toLowerCase()
		if (ownerDomain && ownerDomain !== competitorScan.targetDomain) {
			const ownerScan = await findFreshScan(ownerDomain)
			if (ownerScan) gap = buildGap(competitorScan, ownerScan, ownerDomain)
		}
	}

	return { domain: rawDomain, owner: rawOwner, siteKey, teaser, gap }
}

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	const intent = String(formData.get('intent') ?? 'scan')
	const domain = String(formData.get('domain') ?? '').trim()
	if (!domain) {
		return data(
			{ error: 'Enter a competitor domain to analyze.' },
			{ status: 400 },
		)
	}

	// Bot check before any paid work.
	const ip = getClientIp(request)
	const token = formData.get('cf-turnstile-response')
	const passed = await verifyTurnstile(
		typeof token === 'string' ? token : null,
		ip ?? undefined,
	)
	if (!passed) {
		return data(
			{ error: 'That bot check did not pass. Please try again.' },
			{ status: 400 },
		)
	}

	// Rate limit + global spend ceiling are enforced atomically inside
	// getOrCreateScan (only on a cache MISS), so cached domains are never
	// rejected. We just need the requester key here.
	const requesterKey = requesterKeyFromIp(ip ?? 'unknown')

	// A failed scan is a funnel drop we can only see from the server, which is
	// the only place that knows WHY (rate limit vs spend ceiling vs provider).
	const distinctId = getPosthogDistinctId(request)

	// Own-domain gap: scan the visitor's own domain, then land on the pairing.
	if (intent === 'gap') {
		const owner = String(formData.get('owner') ?? '').trim()
		if (!owner) {
			return data(
				{ error: 'Enter your domain to see your gap.' },
				{ status: 400 },
			)
		}
		try {
			const ownerScan = await getOrCreateScan(owner, {
				source: 'free-checker-gap',
				requesterKey,
			})
			return redirect(
				`/free-backlink-checker?domain=${encodeURIComponent(
					domain,
				)}&owner=${encodeURIComponent(ownerScan.targetDomain)}`,
			)
		} catch (err) {
			return scanErrorResponse(
				err,
				'We could not analyze your domain. Double-check it and try again.',
				{ distinctId, step: 'gap' },
			)
		}
	}

	// Default: scan the competitor.
	try {
		const scan = await getOrCreateScan(domain, {
			source: 'free-checker',
			requesterKey,
		})
		// Post/Redirect/Get: land on a shareable, refresh-safe URL that renders
		// the (now cached) teaser via the loader.
		return redirect(
			`/free-backlink-checker?domain=${encodeURIComponent(scan.targetDomain)}`,
		)
	} catch (err) {
		return scanErrorResponse(
			err,
			'We could not analyze that domain. Double-check it and try again.',
			{ distinctId, step: 'scan' },
		)
	}
}

export default function FreeBacklinkChecker({
	loaderData,
	actionData,
}: Route.ComponentProps) {
	const { domain, siteKey, teaser, gap } = loaderData
	const error = actionData?.error

	// A scan that found nothing is a real, complete result, not a failure: the
	// scan ran, the header's 0 is true, and it stays on the page. What it cannot
	// support is everything built on having a list. The gap form would spend a
	// paid pull on the visitor's own domain to compare it against nothing, and
	// the gate would trade an email for an empty document.
	const emptyProfile = teaser !== null && teaser.totalReferringDomains === 0

	// Step 1 of the pre-account funnel. Keyed on the scanned domain so a second
	// scan in the same session counts as a second view (the component never
	// unmounts across the POST→redirect).
	useTrackOnce('scan_viewed', teaser?.target, {
		total_referring_domains: teaser?.totalReferringDomains,
		high_authority: teaser?.highAuthority,
		new_last_30d: teaser?.newLast30d,
	})

	return (
		<div className="bg-canvas text-ink min-h-screen font-sans">
			<Header />
			<main className="mx-auto max-w-[1080px] px-6 py-14 sm:px-8">
				<div className="mx-auto max-w-[640px] text-center">
					<Eyebrow className="mb-4">Free tool</Eyebrow>
					<Heading
						as="h1"
						className="text-[clamp(32px,5vw,48px)] leading-[1.05] tracking-[-0.03em]"
					>
						Competitor backlink checker
					</Heading>
					<p className="mt-4 text-xl leading-relaxed text-pretty text-[#57565E]">
						See who links to any competitor. Get a clear map of their referring
						domains, with no account and no dashboard.
					</p>

					<Form
						method="post"
						onSubmit={(event) =>
							trackClient('scan_submitted', {
								has_prior_scan: Boolean(teaser),
								domain:
									String(
										new FormData(event.currentTarget).get('domain') ?? '',
									).trim() || undefined,
							})
						}
						className="border-hairline mx-auto mt-7 flex max-w-[500px] flex-col gap-2.5 rounded-2xl border bg-white p-2 shadow-[0_1px_2px_rgba(21,21,25,.04),0_14px_34px_-20px_rgba(21,21,25,.2)]"
					>
						<div className="flex gap-2.5">
							<div className="flex flex-1 items-center gap-2.5 pl-3.5">
								<Icon
									name="magnifying-glass"
									className="size-[18px] text-[#B5B1A8]"
								/>
								<label htmlFor="checker-domain" className="sr-only">
									Competitor domain to analyze
								</label>
								<input
									id="checker-domain"
									name="domain"
									defaultValue={domain ?? ''}
									placeholder="competitor-domain.com"
									spellCheck={false}
									autoComplete="off"
									className="text-ink w-full flex-1 border-none bg-transparent py-3 font-mono text-base outline-none"
								/>
							</div>
							<Button
								type="submit"
								size="lg"
								className="h-auto rounded-[11px] px-6 py-3 text-[15.5px]"
							>
								Map the links
							</Button>
						</div>
						{siteKey ? <TurnstileWidget siteKey={siteKey} /> : null}
					</Form>

					{error ? (
						<p className="text-lost mt-3 text-sm font-medium" role="alert">
							{error}
						</p>
					) : null}
				</div>

				{teaser && emptyProfile ? (
					<>
						<ReportHeader
							teaser={teaser}
							kicker="Backlink report"
							countLabel={['referring', 'domains']}
							ariaLabel="Backlink overview"
						/>
						<NoProfileFound domain={teaser.target} />
						{/* The explainers, because this visitor most likely mistyped or
						    scanned the wrong host and is still deciding what to try next. */}
						<WhatItShows />
						<SampleOutput />
					</>
				) : teaser ? (
					<>
						<ReportHeader
							teaser={teaser}
							kicker="Backlink report"
							countLabel={['referring', 'domains']}
							ariaLabel="Backlink overview"
						/>
						<GapSection teaser={teaser} gap={gap} siteKey={siteKey} />
						<ConversionCta teaser={teaser} gap={gap} siteKey={siteKey} />
						<ReferringList
							teaser={teaser}
							heading="Top referring domains"
							ariaLabel="Top referring domains"
						/>
						<ResultsPitch />
						<ResultsProof />
					</>
				) : (
					<>
						<EmptyState hasDomain={Boolean(domain)} />
						<HowItWorks />
						<WhatItShows />
						<SampleOutput />
						<WhyItMatters />
						<FreeVsPaid />
					</>
				)}

				{/* Evergreen depth shown in every state, so crawlers and readers
				    always get the FAQ (matched by FAQPage schema) and the interlinks. */}
				<Faq />
				<LearnMore />
			</main>
			<Footer />
		</div>
	)
}

// Step 3: the own-domain gap, treated as an editorial moment (big type +
// pill chips), not a boxed card.
function GapSection({
	teaser,
	gap,
	siteKey,
}: {
	teaser: Teaser
	gap: Gap | null
	siteKey: string | null
}) {
	// The gap is the hook: reaching it is the step that predicts an email.
	useTrackOnce(
		'gap_viewed',
		gap ? `${teaser.target}|${gap.comparedTo}` : null,
		{
			gap_count: gap?.count,
			total_referring_domains: teaser.totalReferringDomains,
		},
	)

	if (gap) {
		const remaining = Math.max(gap.count - gap.top.length, 0)
		return (
			<section className="mt-14 text-center" aria-label="Your backlink gap">
				<div className="text-brand font-mono text-[11px] tracking-[0.18em] uppercase">
					Your gap
				</div>
				<p className="font-display text-ink mx-auto mt-3 max-w-[720px] text-[clamp(22px,3.2vw,34px)] leading-[1.15] font-semibold tracking-[-0.01em]">
					<span className="text-brand">{gap.count.toLocaleString()}</span>{' '}
					referring domains link to {teaser.target} but not to{' '}
					<span className="whitespace-nowrap">{gap.comparedTo}</span> yet.
				</p>
				<p className="mx-auto mt-2 max-w-[520px] text-[15px] text-[#57565E]">
					Each one is a link you could realistically go earn too.
				</p>
				{gap.top.length ? (
					<div className="mx-auto mt-6 flex max-w-[760px] flex-wrap justify-center gap-2">
						{gap.top.map((row) => (
							<span
								key={row.domain}
								className="border-hairline text-ink inline-flex items-center gap-2 rounded-full border bg-white px-3.5 py-1.5 font-mono text-[12.5px]"
							>
								{row.domain}
								<span className="text-[#8A867D]">AR {row.authority}</span>
							</span>
						))}
						{remaining > 0 ? (
							<span className="bg-brand-wash text-brand inline-flex items-center rounded-full px-3.5 py-1.5 font-mono text-[12.5px] font-medium">
								+{remaining.toLocaleString()} more
							</span>
						) : null}
					</div>
				) : null}
			</section>
		)
	}

	return (
		<section className="mt-14 text-center" aria-label="See your gap">
			<h3 className="font-display text-ink text-[clamp(20px,2.6vw,28px)] font-semibold tracking-[-0.01em]">
				See exactly what you are missing
			</h3>
			<p className="mx-auto mt-2 max-w-[520px] text-[15px] text-[#57565E]">
				Enter your domain and we will reveal the referring domains linking to{' '}
				{teaser.target} that are not linking to you yet.
			</p>
			<Form
				method="post"
				preventScrollReset
				onSubmit={() => trackClient('gap_submitted', { domain: teaser.target })}
				className="mx-auto mt-5 flex max-w-[420px] flex-col gap-3"
			>
				<input type="hidden" name="intent" value="gap" />
				<input type="hidden" name="domain" value={teaser.target} />
				<div className="border-hairline focus-within:border-brand flex items-center gap-2 rounded-full border bg-white p-1.5 pl-4 transition-colors">
					<label htmlFor="owner-domain" className="sr-only">
						Your domain
					</label>
					<input
						id="owner-domain"
						name="owner"
						placeholder="your-domain.com"
						spellCheck={false}
						autoComplete="off"
						required
						className="text-ink min-w-0 flex-1 border-none bg-transparent font-mono text-sm outline-none"
					/>
					<Button
						type="submit"
						className="h-9 rounded-full px-5 whitespace-nowrap"
					>
						Reveal my gap
					</Button>
				</div>
				{siteKey ? <TurnstileWidget siteKey={siteKey} /> : null}
			</Form>
		</section>
	)
}

// The single saturated moment on the page: a violet band. Soft gate + parallel
// exits (email OR account OR pricing).
function ConversionCta({
	teaser,
	gap,
	siteKey,
}: {
	teaser: Teaser
	gap: Gap | null
	siteKey: string | null
}) {
	const fetcher = useFetcher<{
		ok?: boolean
		error?: string
		reportId?: string
	}>()
	const sent = fetcher.data?.ok === true
	const error = fetcher.data?.error
	const pending = fetcher.state !== 'idle'
	const reportId = fetcher.data?.reportId

	// Effect rather than an onSubmit handler because the Ads conversion must
	// track the SUCCESSFUL response, not the attempt — a fetcher has no success
	// callback, so reacting to its data is the only correct trigger.
	//
	// Keyed on the report id, NOT the scanned domain. This component stays
	// mounted while the teaser behind it changes (scanning a second competitor
	// re-renders rather than remounts), so the previous submission's `ok: true`
	// is still in `fetcher.data`. A domain-keyed effect would re-run against
	// that stale success and invent a lead nobody submitted. A new report id
	// only exists when a new gate submission actually succeeded.
	useEffect(() => {
		if (!reportId) return
		fireAdsConversion({
			label: ADS_CONVERSION_LABELS.lead,
			dedupeKey: `lead_${reportId}`,
		})
	}, [reportId])

	return (
		<section className="mt-14" aria-label="Get the full report">
			<div className="bg-brand relative overflow-hidden rounded-[28px] px-7 py-9 text-center text-white sm:px-10">
				<div className="pointer-events-none absolute -bottom-24 -left-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
				<div className="relative">
					{sent ? (
						<>
							<h3 className="font-display text-[clamp(22px,3vw,30px)] font-semibold tracking-[-0.01em]">
								Check your inbox
							</h3>
							<p className="mx-auto mt-2 max-w-[520px] text-[14.5px] text-white/80">
								We just emailed your full report. It has the complete
								referring-domain list, a CSV, and the domains you are missing.
							</p>
							<p className="mt-4 text-[13px] text-white/70">
								Want to save your reports and come back to them?{' '}
								<Link
									to="/signup"
									className="font-medium text-white underline underline-offset-2"
								>
									Create a free account
								</Link>
								.
							</p>
						</>
					) : (
						<>
							<h3 className="font-display text-[clamp(22px,3vw,30px)] font-semibold tracking-[-0.01em]">
								Get your full report, free
							</h3>
							<p className="mx-auto mt-2 max-w-[560px] text-[14.5px] text-white/80">
								{gap
									? `Enter your email and we will send the full list of ${gap.count.toLocaleString()} domains you are missing, the complete referring-domain list, and a CSV.`
									: `You are seeing the top ${teaser.rows.length} of ${teaser.totalReferringDomains.toLocaleString()} referring domains. Enter your email for the complete list, a CSV, and the domains you are missing.`}
							</p>

							{!gap ? (
								<p className="mx-auto mt-2 max-w-[460px] text-[12.5px] text-white/70">
									Tip: enter your domain in the section above and your gap will
									be included in the report.
								</p>
							) : null}

							<fetcher.Form
								method="post"
								action="/resources/scan-report"
								onSubmit={(event) =>
									trackClient('report_requested', {
										domain: teaser.target,
										has_gap: Boolean(gap),
										gap_count: gap?.count,
										marketing_consent:
											new FormData(event.currentTarget).get('consent') === 'on',
									})
								}
								className="mx-auto mt-5 flex max-w-[460px] flex-col gap-3"
							>
								<input type="hidden" name="domain" value={teaser.target} />
								{gap ? (
									<input type="hidden" name="owner" value={gap.comparedTo} />
								) : null}
								<input type="hidden" name="source" value="free-checker" />
								<div className="flex flex-col gap-2 sm:flex-row">
									<label htmlFor="report-email" className="sr-only">
										Email address for your report
									</label>
									<input
										id="report-email"
										name="email"
										type="email"
										required
										placeholder="you@company.com"
										className="h-11 flex-1 rounded-full border border-white/20 bg-white/10 px-4 font-mono text-sm text-white outline-none placeholder:text-white/50 focus:border-white/50"
									/>
									<Button
										type="submit"
										disabled={pending}
										className="text-brand h-11 rounded-full bg-white px-6 font-medium whitespace-nowrap hover:bg-white/90 disabled:opacity-70"
									>
										{pending ? 'Sending...' : 'Email my report'}
									</Button>
								</div>
								<label className="mx-auto flex max-w-[420px] items-start gap-2 text-left text-[12.5px] text-white/70">
									<input
										type="checkbox"
										name="consent"
										className="mt-0.5 accent-white"
									/>
									<span>
										Also send me occasional tips on winning backlinks. No spam,
										unsubscribe anytime.
									</span>
								</label>
								{siteKey ? <TurnstileWidget siteKey={siteKey} /> : null}
								{error ? (
									<p
										className="text-[13px] font-medium text-white"
										role="alert"
									>
										{error}
									</p>
								) : null}
							</fetcher.Form>

							<p className="mt-4 text-[13px] text-white/70">
								Ready to go further?{' '}
								<Link
									to="/signup"
									className="font-medium text-white underline underline-offset-2"
								>
									Create a free account
								</Link>{' '}
								or{' '}
								<Link
									to="/pricing"
									className="font-medium text-white underline underline-offset-2"
								>
									see pricing
								</Link>
								.
							</p>
						</>
					)}
				</div>
			</div>
		</section>
	)
}

// What stands in for the gap form and the email gate when the scan found
// nothing. Deliberately not styled as an error: the scan worked, and the answer
// it gives is a legitimate one that some sites really do have.
function NoProfileFound({ domain }: { domain: string }) {
	const parent = parentDomainHint(domain)
	return (
		<section className="mt-14 text-center" aria-label="No referring domains">
			<h2 className="font-display text-ink mx-auto max-w-[720px] text-[clamp(20px,2.6vw,28px)] leading-[1.15] font-semibold tracking-[-0.01em]">
				We found no referring domains for{' '}
				<span className="break-all">{domain}</span>.
			</h2>
			<p className="mx-auto mt-3 max-w-[560px] text-[15px] text-[#57565E]">
				{parent
					? `Nothing links to this host yet. Subdomains like this one often sit behind the main site, so if you meant ${parent}, scan that instead.`
					: 'Nothing links to this domain yet. That is a real result for a new site, and it also happens on a typo, so it is worth a second look at the spelling.'}
			</p>
			<p className="mx-auto mt-4 max-w-[560px] text-[14px] text-[#8A867D]">
				There is no report to email for a profile with nothing in it. Scan
				another domain above and we will map it.
			</p>
		</section>
	)
}

function EmptyState({ hasDomain }: { hasDomain: boolean }) {
	return (
		<div className="mx-auto mt-14 max-w-[520px] text-center">
			<Icon
				name="magnifying-glass"
				className="text-brand/40 mx-auto mb-4 size-8"
			/>
			<p className="font-display text-ink text-lg font-semibold">
				{hasDomain
					? 'Hit Map the links to run the analysis.'
					: 'Enter a competitor domain to see who links to them.'}
			</p>
			<p className="mt-1.5 text-[14px] text-[#8A867D]">
				Free and instant. We pull their referring domains live.
			</p>
		</div>
	)
}

// Below-the-table education + sell: reframe what they just saw, spell out what
// the full report and an account unlock, and close with a clear next step.
function ResultsPitch() {
	return (
		<section className="mt-16" aria-label="Why this matters">
			<div className="mx-auto max-w-[700px] text-center">
				<div className="text-brand font-mono text-[11px] tracking-[0.18em] uppercase">
					Why this matters
				</div>
				<h3 className="font-display text-ink mt-3 text-[clamp(24px,3.4vw,36px)] leading-[1.1] font-semibold tracking-[-0.02em]">
					A backlink is a vote. You are looking at your competitor&rsquo;s whole
					ballot box.
				</h3>
				<p className="mx-auto mt-4 max-w-[580px] text-[16px] leading-relaxed text-[#57565E]">
					Every domain that links to a competitor is a tested, repeatable
					tactic, and roughly 90% of them are links you could realistically earn
					too. Getting the data was never the hard part. Knowing which links to
					chase, and how, is. That is what the full report is for.
				</p>
			</div>

			<div className="mx-auto mt-10 grid max-w-[860px] gap-x-8 gap-y-7 sm:grid-cols-2">
				{includes.map((item) => (
					<div key={item.title} className="flex gap-4">
						<div className="bg-brand/[0.1] text-brand flex size-10 shrink-0 items-center justify-center rounded-xl">
							<Icon name={item.icon} className="size-5" />
						</div>
						<div>
							<div className="font-display text-ink text-[16px] font-semibold">
								{item.title}
							</div>
							<p className="mt-1 text-[14px] leading-relaxed text-[#57565E]">
								{item.text}
							</p>
						</div>
					</div>
				))}
			</div>

			<div className="mt-11 text-center">
				<Link
					to="/signup"
					className="bg-brand inline-flex h-12 items-center rounded-full px-8 font-medium text-white transition-colors hover:opacity-90"
				>
					Create your free account
				</Link>
				<p className="mt-3 text-[13px] text-[#8A867D]">
					Free to start.{' '}
					<Link
						to="/pricing"
						className="text-brand underline underline-offset-2"
					>
						See what upgrades cost
					</Link>
					.
				</p>
			</div>
		</section>
	)
}

// --- Evergreen content sections (cold-state depth for readers + crawlers) ---
//
// These are composed as a deliberate SEQUENCE, not a stack of identical bands:
// a bordered stepper panel → an open asymmetric metric grid → a dark tonal
// "why" moment → an open two-column FAQ accordion → link rows. Each section
// changes its container language so adjacent sections never read the same.

// Step 1 shell: a bordered white panel with a hairline-split stepper and big
// ghost numerals, so it reads as one contained "how" object.
function HowItWorks() {
	return (
		<section className="mt-16" aria-label="How the checker works">
			<div className="border-hairline relative overflow-hidden rounded-[28px] border bg-white p-6 sm:p-10">
				<div className="from-brand-wash/70 pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b to-transparent" />
				<div className="relative">
					<div className="max-w-[560px]">
						<SectionKicker>How it works</SectionKicker>
						<h2 className="font-display text-ink mt-4 text-[clamp(24px,3.4vw,34px)] leading-[1.08] font-semibold tracking-[-0.02em]">
							From a competitor&rsquo;s domain to your next link
						</h2>
						<p className="mt-3 text-[15px] leading-relaxed text-[#57565E]">
							No spreadsheets, no dashboard to learn. Three steps, and the third
							one is the whole point.
						</p>
					</div>

					<ol className="border-hairline bg-hairline mt-9 grid gap-px overflow-hidden rounded-2xl border sm:grid-cols-3">
						{steps.map((step, i) => (
							<li
								key={step.title}
								className="group hover:bg-brand-wash/30 relative bg-white p-6 transition-colors"
							>
								<div className="flex items-center justify-between">
									<span className="font-display text-brand/15 text-[44px] leading-none font-semibold tabular-nums">
										0{i + 1}
									</span>
									<span className="bg-brand/[0.1] text-brand flex size-9 items-center justify-center rounded-xl transition-transform group-hover:-translate-y-0.5">
										<Icon name={step.icon} className="size-[18px]" />
									</span>
								</div>
								<div className="font-display text-ink mt-6 text-[16px] font-semibold">
									{step.title}
								</div>
								<p className="mt-1.5 text-[14px] leading-relaxed text-[#57565E]">
									{step.text}
								</p>
							</li>
						))}
					</ol>
				</div>
			</div>
		</section>
	)
}

// Step 2 shell: open (no outer box) + asymmetric — a narrow heading column and
// a 2×2 grid of bordered stat cards. Contrasts the closed panel above.
function WhatItShows() {
	return (
		<section
			className="mt-24 grid gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:gap-14"
			aria-label="What the free checker shows"
		>
			<div className="lg:pt-3">
				<SectionKicker>What you get</SectionKicker>
				<h2 className="font-display text-ink mt-4 text-[clamp(24px,3.4vw,34px)] leading-[1.06] font-semibold tracking-[-0.02em]">
					A competitor&rsquo;s whole profile, in plain language
				</h2>
				<p className="mt-4 max-w-[420px] text-[15px] leading-relaxed text-[#57565E]">
					Every metric is defined right on the page and none of it needs a
					manual. Here is what the checker returns the moment you run it, free.
				</p>
			</div>

			<div className="grid gap-4 sm:grid-cols-2">
				{shows.map((item) => (
					<div
						key={item.title}
						className="border-hairline group rounded-2xl border bg-white p-5 transition-all hover:-translate-y-0.5 hover:shadow-[0_1px_2px_rgba(21,21,25,.04),0_18px_40px_-24px_rgba(21,21,25,.28)]"
					>
						<span className="bg-brand/[0.1] text-brand flex size-10 items-center justify-center rounded-xl">
							<Icon name={item.icon} className="size-5" />
						</span>
						<div className="font-display text-ink mt-4 text-[15.5px] font-semibold">
							{item.title}
						</div>
						<p className="mt-1.5 text-[13.5px] leading-relaxed text-[#57565E]">
							{item.text}
						</p>
					</div>
				))}
			</div>
		</section>
	)
}

// Sits between the "what you get" cards and the "why" band: the cards describe
// the columns, this shows them populated. Tool-intent visitors want to see the
// output before they hand over a domain, and a page that only describes it in
// prose loses them to one that shows it.
function SampleOutput() {
	return (
		<section className="mt-24" aria-label="Example output">
			<div className="max-w-[620px]">
				<SectionKicker>Example output</SectionKicker>
				<h2 className="font-display text-ink mt-4 text-[clamp(24px,3.4vw,34px)] leading-[1.06] font-semibold tracking-[-0.02em]">
					What a report looks like when it comes back
				</h2>
				<p className="mt-4 text-[15px] leading-relaxed text-[#57565E]">
					Referring domains, strongest first. Below is the format, filled in
					with example rows so you can see the shape of it before you run
					anything.
				</p>
			</div>

			<div className="mt-9">
				<div className="border-hairline flex items-baseline justify-between border-b pb-3">
					<h3 className="font-display text-ink text-[17px] font-semibold">
						Referring domains
					</h3>
					<span className="font-mono text-xs text-[#9A968C]">
						illustrative example
					</span>
				</div>
				{sampleRows.map((row) => (
					<div
						key={row.domain}
						className="grid grid-cols-[1fr_auto] items-center gap-3 border-b border-[#EFEAE6] px-2 py-3 last:border-b-0 sm:grid-cols-[1fr_64px_96px_120px] sm:gap-4"
					>
						<span className="text-ink flex min-w-0 items-center gap-2 font-mono text-[14px]">
							<span className="truncate">{row.domain}</span>
							{row.isNew ? (
								<BrandBadge tone="new" size="sm">
									New
								</BrandBadge>
							) : null}
						</span>
						<span className="font-mono text-[14px] text-[#57565E]">
							AR {row.authority}
						</span>
						<span
							className={`hidden font-mono text-[13px] font-medium sm:block ${
								row.dofollow ? 'text-gained-ink' : 'text-[#C2410C]'
							}`}
						>
							{row.dofollow ? 'dofollow' : 'nofollow'}
						</span>
						<span className="hidden justify-self-end sm:block">
							<BrandBadge tone="stable" size="sm">
								{row.links.toLocaleString()} links
							</BrandBadge>
						</span>
					</div>
				))}
				<p className="mt-3 px-2 font-mono text-[11px] text-[#9A968C]">
					AR = Authority Rank (0 to 100). Higher is stronger.
				</p>
			</div>

			<div className="mt-10">
				<h3 className="font-display text-ink text-[17px] font-semibold">
					How to read it
				</h3>
				<div className="mt-4 grid gap-6 sm:grid-cols-3">
					{sampleReadings.map((reading) => (
						<div key={reading.label}>
							<div className="text-brand font-mono text-[11px] tracking-[0.14em] uppercase">
								{reading.label}
							</div>
							<p className="mt-2 text-[14px] leading-relaxed text-[#57565E]">
								{reading.text}
							</p>
						</div>
					))}
				</div>
			</div>
		</section>
	)
}

// The honesty section, and the one that converts the "free tool" searcher who
// is deciding whether to bother with a paid suite at all. Saying plainly what
// free does not cover is the reason to believe the rest of the page.
function FreeVsPaid() {
	return (
		<section
			className="mt-24 grid gap-10 lg:grid-cols-[0.72fr_1.28fr] lg:gap-14"
			aria-label="What free covers"
		>
			<div className="lg:pt-3">
				<SectionKicker>Free vs paid</SectionKicker>
				<h2 className="font-display text-ink mt-4 text-[clamp(24px,3.4vw,34px)] leading-[1.06] font-semibold tracking-[-0.02em]">
					When free is genuinely enough
				</h2>
				<p className="mt-4 max-w-[380px] text-[15px] leading-relaxed text-[#57565E]">
					Most people researching a handful of competitors never need a
					subscription. It is worth knowing where the line actually falls.
				</p>
			</div>

			<div className="grid gap-4 sm:grid-cols-2">
				<div className="border-hairline rounded-2xl border bg-white p-6">
					<div className="text-gained-ink font-mono text-[11px] tracking-[0.14em] uppercase">
						Free covers this
					</div>
					<ul className="mt-4 grid gap-3">
						{[
							'Reading any competitor’s referring domains end to end',
							'Finding your gap against them, ranked by what you can win',
							'Checking a handful of rivals before a campaign',
							'Deciding which links are worth chasing this quarter',
						].map((item) => (
							<li key={item} className="flex gap-2.5">
								<Icon
									name="check"
									className="text-gained-ink mt-0.5 size-4 shrink-0"
								/>
								<span className="text-[14px] leading-relaxed text-[#57565E]">
									{item}
								</span>
							</li>
						))}
					</ul>
				</div>

				<div className="border-hairline rounded-2xl border bg-white p-6">
					<div className="font-mono text-[11px] tracking-[0.14em] text-[#8A867D] uppercase">
						You need more than free when
					</div>
					<ul className="mt-4 grid gap-3">
						{[
							'You want daily alerts the moment a rival gains or loses a link',
							'You need years of historical trend data, not today’s snapshot',
							'You are auditing hundreds of domains, or reporting to clients',
							'You want link intersect across ten or more competitors at once',
						].map((item) => (
							<li key={item} className="flex gap-2.5">
								<Icon
									name="arrow-right"
									className="mt-0.5 size-4 shrink-0 text-[#B5B1A8]"
								/>
								<span className="text-[14px] leading-relaxed text-[#57565E]">
									{item}
								</span>
							</li>
						))}
					</ul>
					<p className="mt-5 text-[13.5px] leading-relaxed text-[#8A867D]">
						When you do cross that line, our paid reports are a one-off purchase
						rather than a subscription.{' '}
						<Link
							to="/pricing"
							className="text-brand font-medium underline underline-offset-2"
						>
							See pricing
						</Link>
						, or read the{' '}
						<Link
							to="/best-tools/free-ahrefs-alternatives"
							className="text-brand font-medium underline underline-offset-2"
						>
							free Ahrefs alternatives
						</Link>{' '}
						comparison first.
					</p>
				</div>
			</div>
		</section>
	)
}

// Step 3 shell: the memorable moment — a dark ink band with a giant "~90%"
// figure. The single tonal break in an otherwise light page.
function WhyItMatters() {
	return (
		<section className="mt-24" aria-label="Why competitor backlinks matter">
			<div className="bg-ink relative overflow-hidden rounded-[28px] px-7 py-12 text-white sm:px-12 sm:py-16">
				<div className="bg-brand/25 pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full blur-3xl" />
				<div className="bg-gained/10 pointer-events-none absolute -bottom-28 -left-16 h-72 w-72 rounded-full blur-3xl" />
				<div className="relative grid items-center gap-10 lg:grid-cols-[auto_1fr] lg:gap-16">
					<div className="text-center lg:text-left">
						<div className="font-display text-brand-light text-[clamp(76px,13vw,140px)] leading-[0.85] font-semibold tracking-[-0.04em]">
							~90%
						</div>
						<div className="mt-3 font-mono text-[11px] tracking-[0.16em] text-white/45 uppercase">
							of a rival&rsquo;s links are reproducible
						</div>
					</div>
					<div>
						<h2 className="font-display text-[clamp(22px,3vw,32px)] leading-[1.14] font-semibold tracking-[-0.02em] text-white">
							A backlink is a vote. A competitor&rsquo;s links are a ballot box
							you can copy from.
						</h2>
						<p className="mt-4 max-w-[520px] text-[15px] leading-relaxed text-white/65">
							Google ranks pages partly on who vouches for them, and most of a
							competitor&rsquo;s links are reproducible tactics: a guest post, a
							resource page, a tool roundup, a podcast. Each referring domain
							you see is a site that already links to a business like yours,
							which makes it the most realistic link you could earn next.
							Getting the data was never the hard part. Knowing which links to
							chase is, and that is exactly what your gap tells you.
						</p>
					</div>
				</div>
			</div>
		</section>
	)
}

// Step 4 shell: open two-column with a real accordion (native details/summary,
// CSS-only — no JS, CSP-safe). Genuine open/close UX, not a static list.
function Faq() {
	return (
		<section
			className="mt-24 grid gap-10 lg:grid-cols-[0.62fr_1.38fr] lg:gap-14"
			aria-label="Frequently asked questions"
		>
			<div>
				<SectionKicker>Questions</SectionKicker>
				<h2 className="font-display text-ink mt-4 text-[clamp(24px,3.4vw,34px)] leading-[1.06] font-semibold tracking-[-0.02em]">
					Everything you might be wondering
				</h2>
				<p className="mt-4 max-w-[320px] text-[15px] leading-relaxed text-[#57565E]">
					Still stuck? Read the full{' '}
					<Link
						to="/guides/find-competitor-backlinks"
						className="text-brand font-medium underline underline-offset-2"
					>
						competitor backlinks guide
					</Link>
					.
				</p>
			</div>

			<FaqAccordion faqs={FAQS} />
		</section>
	)
}

// Step 5 shell: link rows (not a bare list) with a hover slide, grouped into
// Guides / Comparisons — the crawl paths into each cluster.
function LearnMore() {
	const columns = [
		{ heading: 'Guides', links: learnGuides },
		{ heading: 'Compare the tools', links: learnComparisons },
	]
	return (
		<section
			className="border-hairline mt-24 border-t pt-12"
			aria-label="Keep learning"
		>
			<div className="mb-8 flex flex-wrap items-baseline gap-x-4 gap-y-1">
				<h2 className="font-display text-ink text-[clamp(20px,2.6vw,26px)] font-semibold tracking-[-0.01em]">
					Keep going
				</h2>
				<span className="text-[14px] text-[#8A867D]">
					Guides and comparisons to turn a list of links into a plan.
				</span>
			</div>
			<div className="grid gap-x-14 gap-y-9 sm:grid-cols-2">
				{columns.map((col) => (
					<div key={col.heading}>
						<div className="text-brand mb-1 font-mono text-[11px] tracking-[0.18em] uppercase">
							{col.heading}
						</div>
						<ul>
							{col.links.map((l) => (
								<li key={l.href}>
									<Link
										to={l.href}
										className="group border-hairline flex items-center justify-between gap-4 border-b py-3.5 last:border-b-0"
									>
										<span className="text-ink group-hover:text-brand text-[15.5px] font-medium transition-colors">
											{l.title}
										</span>
										<Icon
											name="arrow-right"
											className="group-hover:text-brand size-4 text-[#B5B1A8] transition-transform group-hover:translate-x-1"
										/>
									</Link>
								</li>
							))}
						</ul>
					</div>
				))}
			</div>
		</section>
	)
}

function ResultsProof() {
	return (
		<section
			className="border-hairline mt-16 border-t pt-14"
			aria-label="What people say"
		>
			<div className="grid gap-8 md:grid-cols-3">
				{quotes.map((q) => (
					<figure key={q.name}>
						<blockquote className="font-display text-ink text-[17px] leading-snug">
							&ldquo;{q.text}&rdquo;
						</blockquote>
						<figcaption className="mt-3 text-[13px] text-[#8A867D]">
							{q.name}, {q.role}
						</figcaption>
					</figure>
				))}
			</div>
		</section>
	)
}
